import { ApiOptions } from '../../api-options.decorator';
import { DefaultParams } from '../../default-params.model';
import { ParamsAdapter } from '../../params.adapter';
import { RoleCapacity } from '../../role-capacity';

import { ApiBaseContract } from './base';

export class ApiContract extends ApiBaseContract {
  /**
   * It will get all active and inactive delivery points
   */
  @ApiOptions({ channel: 'deliveryPoint.get' })
  getContractAllDeliveryPoints(params: ParamsAdapter) {
    return this.get('/contract/points/in-delivery', params);
  }

  @ApiOptions({ channel: 'invoice.publish', requestType: 'json', interceptErrors: false })
  publishInvoiceManually(params: ParamsAdapter, id: number) {
    return this.post(`/contract/${id}/invoice/save`, params);
  }

  @ApiOptions({ channel: 'invoice.publish', requestType: 'json' })
  publishInvoiceByDeliveryPoint(gridOperator: string, meteringPoint: string, params: ParamsAdapter) {
    return this.post(`/contract/point/${gridOperator}/${meteringPoint}/invoice/save`, params);
  }

  @ApiOptions({ channel: 'contract.patch', interceptErrors: false })
  editContract(contractId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}`, params);
  }

  @ApiOptions({ channel: 'contract.post', interceptErrors: false })
  createContract(params: ParamsAdapter) {
    return this.post(`/contract`, params);
  }

  @ApiOptions({ channel: 'contract.get' })
  getContract(id: number) {
    return this.get(`/contract/${id}`);
  }

  @ApiOptions({ channel: 'contract.list' })
  getContracts(params: ParamsAdapter = new DefaultParams()) {
    return this.get('/contract', params);
  }

  @ApiOptions({ channel: 'contractDeliveryPointSetting.get', capacities: [RoleCapacity.apiContractBasicRead] })
  getGooProducts(params: ParamsAdapter) {
    return this.get(`/contract/goo/product`, params);
  }

  @ApiOptions({ channel: 'contractDeliveryPointSetting.get', capacities: [RoleCapacity.apiContractBasicRead] })
  getContractDeliveryPointSetting(contractId: number, gridOperator: string, meteringPoint: string) {
    return this.get(`/contract/${contractId}/payment_settings/${gridOperator}/${meteringPoint}`);
  }

  @ApiOptions({
    channel: 'contractDeliveryPointSetting.put',
    capacities: [RoleCapacity.apiContractPaymentSettingsWrite],
  })
  putContractDeliveryPointSetting(contractId: number, gridOperator: string, meteringPoint: string, params: ParamsAdapter) {
    return this.put(`/contract/${contractId}/payment_settings/${gridOperator}/${meteringPoint}`, params);
  }

  @ApiOptions({ channel: 'contractPaymentSetting.get', interceptErrors: false })
  getContractPaymentSetting(contractId: number) {
    return this.get(`/contract/${contractId}/payment_settings`);
  }

  @ApiOptions({
    channel: 'contractPaymentSetting.modify',
    capacities: [RoleCapacity.apiContractPaymentSettingsWrite],
    interceptErrors: false,
  })
  putContractPaymentSetting(contractId: number, params: ParamsAdapter) {
    return this.put(`/contract/${contractId}/payment_settings`, params);
  }

  @ApiOptions({
    channel: 'contractPaymentSetting.modify',
    capacities: [RoleCapacity.apiContractPaymentSettingsWrite],
    interceptErrors: false,
  })
  patchContractPaymentSetting(contractId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/payment_settings`, params);
  }

  @ApiOptions({ interceptErrors: false })
  getContractPriceStripInstrumentAggregatedPosition(id: number, priceStripId: number, instrumentId: number, params: ParamsAdapter) {
    return this.get(`/contract/${id}/price-strip/${priceStripId}/instrument/${instrumentId}/position/aggregated`, params);
  }

  /**
   * the param 'volume_configuration_code' improves
   * the endpoint performance while requesting InstrumentPositions
   */
  @ApiOptions({ interceptErrors: false })
  getContractPriceStripInstrumentPositions(id: number, priceStripId: number, instrumentId: number, params: ParamsAdapter) {
    return this.get(`/contract/${id}/price-strip/${priceStripId}/instrument/${instrumentId}/position`, params);
  }

  @ApiOptions({ interceptErrors: false })
  getIndexExposure(id: number, priceStripId: number, params: ParamsAdapter) {
    return this.get(`/contract/${id}/price-strip/${priceStripId}/index-exposure`, params);
  }

  @ApiOptions({ channel: 'contractPriceStripInstrumentGet', capacities: [RoleCapacity.apiContractBasicRead] })
  getContractPriceStripInstrument(id: number, priceStripId: number, instrumentId: number) {
    return this.get(`/contract/${id}/price-strip/${priceStripId}/instrument/${instrumentId}`);
  }

  @ApiOptions({ channel: 'contractPriceStripSubCategoryGet' })
  getContractSubCategoryReferences(params: ParamsAdapter) {
    return this.get(`/contract/referential/price-strip-subcategory`, params);
  }

  @ApiOptions({ channel: 'contractPriceStripFormulasModify', capacities: [RoleCapacity.apiContractFullWrite] })
  saveContractPriceStripFormula(contractId: number, priceStripId: number, timeModelValue: string, params: ParamsAdapter) {
    return this.put(`/contract/${contractId}/price-strip/${priceStripId}/formula/${timeModelValue}`, params);
  }

  @ApiOptions({ channel: 'contractPriceStripFormulasModify', capacities: [RoleCapacity.apiContractFullWrite] })
  deleteContractPriceStripFormula(contractId: number, priceStripId: number, timeModelValue: string) {
    return this.delete(`/contract/${contractId}/price-strip/${priceStripId}/formula/${timeModelValue}`);
  }

  @ApiOptions({ channel: 'draftDelete' })
  deleteDraft(contractId: number) {
    return this.delete(`/contract/${contractId}`);
  }

  @ApiOptions({ interceptErrors: false, capacities: [RoleCapacity.apiReferentialRead] })
  getContractIndexes() {
    return this.get(`/contract/index`);
  }

  @ApiOptions({ channel: 'contractTransaction' })
  duplicateContract(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/duplicate`, params);
  }

  @ApiOptions({ channel: 'volumeExportExcel', responseType: 'blob' })
  exportAllHussartExcel(params: ParamsAdapter) {
    return this.get<Blob>(`/contract/all-hussart/excel`, params);
  }

  @ApiOptions({ channel: 'contractSettings' })
  updateOtActivation(contractId: number, active: boolean) {
    return this.patch(`/contract/${contractId}/settings`, new DefaultParams({ event_management_active: active }));
  }

  @ApiOptions({ channel: 'contractSettings' })
  patchContractSettings(contractId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/settings`, params);
  }

  @ApiOptions({ channel: 'contractSettings' })
  getContractSettings(contractId: number) {
    return this.get(`/contract/${contractId}/settings`);
  }

  @ApiOptions({ channel: 'contractEventSettings' })
  updateOtBookingMode(contractId: number, params: ParamsAdapter) {
    return this.put(`/contract/${contractId}/event/settings`, params);
  }

  @ApiOptions({ channel: 'commodityPriceExcel', responseType: 'blob' })
  getCommodityPriceExcel(contractId: number, priceStripId: number, params: ParamsAdapter) {
    return this.get<Blob>(`/contract/${contractId}/price-strip/${priceStripId}/price/excel`, params);
  }

  @ApiOptions({ channel: 'customSheet' })
  getCustomSheet(contractId: number) {
    return this.get(`/contract/${contractId}/custom-sheet`);
  }

  @ApiOptions({ channel: 'customSheet' })
  saveCustomSheet(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/custom-sheet`, params);
  }

  @ApiOptions({ channel: 'customSheet' })
  updatePriceStripCustomSheet(contractId: number, priceStripId: number, timeModel: string, params: ParamsAdapter) {
    return this.put(`/contract/${contractId}/price-strip/${priceStripId}/formula/${timeModel}`, params);
  }

  @ApiOptions({ channel: 'customSheetValues' })
  getCustomSheetValues(contractId: number, customSheetId: number, params: ParamsAdapter) {
    return this.get(`/contract/${contractId}/custom-sheet/${customSheetId}/value`, params);
  }

  // contract deals
  @ApiOptions({ channel: 'contractOrderCreate' })
  postOrder(contractId: number, priceStripId: number, instrumentId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/price-strip/${priceStripId}/instrument/${instrumentId}/order`, params);
  }

  @ApiOptions({ interceptErrors: false })
  deleteOrder(contractId: number, priceStripId: number, instrumentId: number, orderId: number) {
    return this.delete(`/contract/${contractId}/price-strip/${priceStripId}/instrument/${instrumentId}/order/${orderId}`);
  }

  @ApiOptions({ interceptErrors: false })
  deleteTransaction(contractId: number, transactionId: number) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/cancel-acceptation`);
  }

  @ApiOptions({ channel: 'ordersExportExcel', responseType: 'blob' })
  getExcel(id: number, priceStripId: number, instrumentId: number, params: ParamsAdapter) {
    return this.get<Blob>(`/contract/${id}/price-strip/${priceStripId}/instrument/${instrumentId}/position/order/excel`, params);
  }

  @ApiOptions({ channel: 'contractBrickGet' })
  getBricks() {
    return this.get(`/contract/brick-description`);
  }

  @ApiOptions({ channel: 'contractTransactionType' })
  getTransactionType(contractId: number, withBricks: boolean) {
    const params = new DefaultParams({ with_bricks: withBricks });
    return this.get(`/contract/${contractId}/transaction-type`, params);
  }

  @ApiOptions({ channel: 'contractTransactionType' })
  getTransactionTypeById(contractId: number, transactionTypeId: number, withBricks: boolean) {
    const params = new DefaultParams({ with_bricks: withBricks });
    return this.get(`/contract/${contractId}/transaction-type/${transactionTypeId}`, params);
  }

  @ApiOptions({ channel: 'contractTransactionType' })
  patchTransactionType(contractId: number, transactionTypeId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/transaction-type/${transactionTypeId}`, params);
  }

  @ApiOptions({ channel: 'contractTransactionDefaultedBricks' })
  getTransactionDefaultedBricks(contractId: number, transactionId: number) {
    return this.get(`/contract/${contractId}/transaction/${transactionId}/defaulted-bricks`);
  }

  @ApiOptions({ channel: 'contractTransactionComputedVolumes' })
  getTransactionComputedVolumes(contractId: number, transactionTypeId: number, params: ParamsAdapter) {
    return this.get(`/contract/${contractId}/transaction-type/${transactionTypeId}/compute-volumes`, params);
  }

  @ApiOptions({ channel: 'contractTransactionRules' })
  getTransactionRules(contractId: number, transactionTypeId: number, params: ParamsAdapter) {
    return this.get(`/contract/${contractId}/transaction-type/${transactionTypeId}/rule`, params);
  }

  @ApiOptions({ channel: 'contractTransactionRules' })
  postTransactionRules(contractId: number, transactionTypeId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction-type/${transactionTypeId}/rule`, params);
  }

  @ApiOptions({ channel: 'contractTransactionRules' })
  patchTransactionRules(contractId: number, transactionTypeId: number, ruleId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/transaction-type/${transactionTypeId}/rule/${ruleId}`, params);
  }

  @ApiOptions({ channel: 'contractTransactionRules' })
  deleteTransactionRule(contractId: number, transactionTypeId: number, ruleId: number) {
    return this.delete(`/contract/${contractId}/transaction-type/${transactionTypeId}/rule/${ruleId}`);
  }

  @ApiOptions({ channel: 'contractTransactionMarketExposure' })
  getTransactionMarketExposure(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.get(`/contract/${contractId}/transaction/${transactionId}/market-exposure`, params);
  }

  @ApiOptions({ channel: 'contractTransaction' })
  postTransaction(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction`, params);
  }

  @ApiOptions({ channel: 'contractTransactionCheck', interceptErrors: false })
  postTransactionCheck(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/check`, params);
  }

  @ApiOptions({ channel: 'contractTransaction' })
  postTransactionPop(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction`, params);
  }

  @ApiOptions({ channel: 'contractTransactionCheck', interceptErrors: false })
  postTransactionPopCheck(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/check`, params);
  }

  @ApiOptions({ channel: 'contractTransactionTreat' })
  postTransactionTreat(contractId: number, transactionId: number) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/treat`);
  }

  @ApiOptions({ channel: 'transactions' })
  getTransactions(params: ParamsAdapter) {
    return this.get(`/contract/transaction`, params);
  }

  @ApiOptions({ channel: 'contractTransactions' })
  getContractTransactions(contractId: number, params: ParamsAdapter) {
    return this.get(`/contract/${contractId}/transaction`, params);
  }

  @ApiOptions({ channel: 'contractTransactionConfirmationMail' })
  getTransactionConfirmationMail(transactionId: number) {
    return this.get(`/contract/transaction/${transactionId}/confirmation-mail`, new DefaultParams({ conf_mail_auto: false }));
  }

  @ApiOptions({ channel: 'contractTransactionAccept' })
  postTransactionAccept(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/accept`, params);
  }

  @ApiOptions({ channel: 'contractTransactionPublishProposalSolo' })
  postTransactionPublishProposalSolo(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/publish-proposal-solo`, params);
  }

  @ApiOptions({ channel: 'contractTransactionPublishProposalArenh' })
  postTransactionPublishProposalArenh(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/publish-proposal-arenh`, params);
  }

  @ApiOptions({ channel: 'contractTransactionPublishProposalPhysicalBlock' })
  postTransactionPublishProposalPhysicalBlock(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/publish-proposal-physical-block`, params);
  }

  @ApiOptions({ channel: 'contractTransactionPublishProposalSpread' })
  postTransactionPublishProposalSpread(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/publish-proposal-spread`, params);
  }

  @ApiOptions({ channel: 'contractTransactionPublishProposalPeakOffPeak' })
  postTransactionPublishProposalPeakOffPeak(contractId: number, transactionId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/transaction/${transactionId}/publish-proposal-peak-off-peak`, params);
  }

  //contract aggregation
  // try to add X-Fields to have just the id in the answer. This eliminate the unnecessary request
  @ApiOptions({ interceptErrors: false })
  patchContractHeader(contractId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}`, params);
  }

  @ApiOptions({ interceptErrors: false })
  patchPerimeter(perimeterId: number, contractId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/perimeter/${perimeterId}`, params);
  }

  @ApiOptions({ interceptErrors: false })
  postPerimeter(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/perimeter`, params);
  }

  @ApiOptions({ interceptErrors: false })
  deletePerimeter(perimeterId: number, contractId: number) {
    return this.delete(`/contract/${contractId}/perimeter/${perimeterId}`);
  }

  @ApiOptions({ interceptErrors: false })
  patchPriceStrip(priceStripId: number, contractId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/price-strip/${priceStripId}`, params);
  }

  @ApiOptions({ interceptErrors: false })
  postPriceStrip(contractId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/price-strip`, params);
  }

  @ApiOptions({ interceptErrors: false })
  deletePriceStrip(priceStripId: number, contractId: number) {
    return this.delete(`/contract/${contractId}/price-strip/${priceStripId}`);
  }

  @ApiOptions({ interceptErrors: false })
  postDeliveryPoint(contractId: number, perimeterId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/perimeter/${perimeterId}/point/multi`, params);
  }

  @ApiOptions({ interceptErrors: false })
  //specific to delivery point, we can't patch, just put
  putDeliveryPoint(contractId: number, perimeterId: number, gridOperator: string, meteringPoint: string, params: ParamsAdapter) {
    return this.put(`/contract/${contractId}/perimeter/${perimeterId}/point/${gridOperator}/${meteringPoint}`, params);
  }

  @ApiOptions({ interceptErrors: false })
  deleteDeliveryPoint(contractId: number, perimeterId: number, gridOperator: string, meteringPoint: string) {
    return this.delete(`/contract/${contractId}/perimeter/${perimeterId}/point/${gridOperator}/${meteringPoint}`);
  }

  @ApiOptions({ interceptErrors: false })
  postInstrument(contractId: number, priceStripId: number, params: ParamsAdapter) {
    return this.post(`/contract/${contractId}/price-strip/${priceStripId}/instrument`, params);
  }

  @ApiOptions({ interceptErrors: false, requestType: 'json' })
  patchInstrument(contractId: number, priceStripId: number, instrumentId: number, params: ParamsAdapter) {
    return this.patch(`/contract/${contractId}/price-strip/${priceStripId}/instrument/${instrumentId}`, params);
  }

  @ApiOptions({ interceptErrors: false })
  deleteInstrument(instrumentId: number, contractId: number, priceStripId: number) {
    return this.delete(`/contract/${contractId}/price-strip/${priceStripId}/instrument/${instrumentId}`);
  }
}
